import {
  Box,
  CloseButton,
  ContainerProps,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  InputProps,
  InputRightElement,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  Spinner,
  useDisclosure,
} from '@chakra-ui/react';
import { useCombobox } from 'downshift';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import DefaultOption, { AutocompleteOption } from './DefaultOption';
import { FormFieldMeta } from './forms';
import { ArrowDown } from './icons';

// Debounce utility
const debounce = (func: Function, wait: number) => {
  let timeout: NodeJS.Timeout;
  return function executedFunction(...args: any[]) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

interface DefaultOptionProps {
  inputValue: string;
  isSelected: boolean;
  option: AutocompleteOption;
}

interface AutocompleteProps extends Omit<InputProps, 'onChange' | 'onBlur' | 'filter'> {
  dataTestId?: string;
  initialValue?: AutocompleteOption;
  label?: string;
  placeholder?: string;
  helperText?: string;
  options?: AutocompleteOption[];
  filter?: (option: AutocompleteOption, inputValue: string) => boolean;
  hideChevron?: boolean;
  listMaxH?: string | number;
  Option?: React.FC<DefaultOptionProps>;
  getOptions?: (inputValue: string) => Promise<AutocompleteOption[] | 'skip'>;
  onChange?: (selectedOption: AutocompleteOption | null) => void;
  onBlur?: () => void;
  errors?: Record<string, any>;
  err?: Record<string, any>;
  name: string;

  containerProps?: Record<string, any>;
  searchOptions?: AutocompleteOption[];
  isLoading?: boolean;
  disableUseEffect?: boolean;
  isRequired?: boolean;
  startSearchLength?: number;
  invalid?: boolean;
  rightElement?: React.ReactNode;
  rightElementProps?: Record<string, any>;
  leftElement?: React.ReactNode;
  helperTextStyles?: Record<string, any>;
  optionToValue?: (option: AutocompleteOption) => any;
  valueToOption?: (value: any) => AutocompleteOption;
  onInputChange?: (inputValue: string) => void;
  errorContainerStyles?: ContainerProps;
}

const START_SEARCH_LENGTH = 3;

const Autocomplete: React.FC<AutocompleteProps> = ({
  dataTestId,
  initialValue,
  label,
  placeholder,
  helperText,
  options = [],
  filter,
  hideChevron = false,
  listMaxH = 72,
  Option = DefaultOption,
  getOptions,
  onChange,
  onBlur,
  errors = {},
  err = {},
  name = '',

  containerProps = {},
  searchOptions,
  isLoading,
  disableUseEffect = true,
  isRequired,
  startSearchLength = START_SEARCH_LENGTH,
  invalid,
  rightElement,
  rightElementProps,
  leftElement,
  helperTextStyles,
  onInputChange,
  optionToValue = (option) => option,
  valueToOption = (value) => value,
  errorContainerStyles = {},
  ...inputProps
}) => {
  const [items, setItems] = useState<AutocompleteOption[]>(getOptions ? [] : options);
  const [loading, setLoading] = useState(false);
  const { isOpen, onClose, onOpen } = useDisclosure();

  useEffect(() => {
    if (disableUseEffect) {
      return;
    }
    setLoading(isLoading || false);
    setItems(searchOptions || []);
  }, [isLoading, searchOptions, disableUseEffect]);

  const fetchOptions = useCallback(
    async (value: string) => {
      if (!getOptions) return;

      setLoading(true);
      let newOptions: AutocompleteOption[] = [];

      try {
        const response = await getOptions(value);
        if (response === 'skip') {
          setLoading(false);
          return;
        }
        newOptions = Array.isArray(response) ? response : [];
      } catch (error) {
        newOptions = [];
        console.error('Error fetching options:', error);
      }

      if (!Array.isArray(newOptions)) {
        console.error('getOptions method should return array');
      }

      if (newOptions.length && (!newOptions[0]?.label || !newOptions[0]?.value)) {
        console.error(
          'getOptions method should return array of objects with label and value fields',
        );
      }

      setLoading(false);
      setItems(newOptions);
    },
    [getOptions],
  );

  const debouncedFetchOptions = useMemo(() => debounce(fetchOptions, 500), [fetchOptions]);

  const onInputValueChange = useCallback(
    ({ inputValue, selectedItem }: any) => {
      onInputChange?.(inputValue);

      if (inputValue === selectedItem?.label) return;

      const trimmedInputValue = inputValue.trim();

      if (getOptions) {
        if (inputValue.length < startSearchLength) {
          setItems([]);
          return;
        }
        debouncedFetchOptions(trimmedInputValue);
        return;
      }

      const filterMethod = filter
        ? (option: AutocompleteOption) => filter(option, trimmedInputValue)
        : (option: AutocompleteOption) =>
            option?.label?.toLowerCase().includes(trimmedInputValue.toLowerCase());

      setItems(options.filter(filterMethod));
    },
    [getOptions, startSearchLength, debouncedFetchOptions, filter, options],
  );

  const {
    getToggleButtonProps,
    getLabelProps,
    getMenuProps,
    getInputProps,
    highlightedIndex,
    getItemProps,
    inputValue,
    selectItem,
    selectedItem,
    reset,
    setInputValue,
  } = useCombobox({
    onInputValueChange,
    onSelectedItemChange: ({ selectedItem: selected }) => {
      onChange?.(selected ? optionToValue(selected) : null);
    },
    items,
    itemToString: (option: AutocompleteOption | null) => option?.label ?? '',
    initialInputValue: initialValue ? valueToOption(initialValue).label : '',
    initialSelectedItem: initialValue ? valueToOption(initialValue) : null,
  });

  const handleClear = () => {
    reset();
    onChange?.(null);
  };

  const handleBlur = () => {
    onBlur?.();
  };

  useEffect(() => {
    if (initialValue && inputValue !== initialValue.label && !selectedItem) {
      selectItem(initialValue);
      setInputValue(initialValue.label || '');
    }
  }, [initialValue]);

  const showItems = !!items.length && isOpen;
  const showClearButton = !!inputValue;
  const showChevron = !getOptions && !hideChevron && !showClearButton;

  const isInvalid = invalid || !!errors?.[name || ''] || !!err?.[name || ''];

  const getInputPaddingRight = () => {
    if ((showClearButton || showChevron) && loading) return '4xl';
    if (showClearButton || showChevron || loading) return '2xl';
    return 'md';
  };

  return (
    <FormControl
      isInvalid={isInvalid}
      isRequired={isRequired}
      w='full'
      {...containerProps}
    >
      {label && <FormLabel {...getLabelProps()}>{label}</FormLabel>}
      <Box
        position='relative'
        w='full'
      >
        <InputGroup onClick={onOpen}>
          <Input
            {...getInputProps()}
            data-testid={dataTestId}
            id={name}
            placeholder={placeholder}
            pr={getInputPaddingRight()}
            onBlur={handleBlur}
            {...inputProps}
          />
          {leftElement && <InputLeftElement maxH='full'>{leftElement}</InputLeftElement>}
          <InputRightElement
            pr='xs'
            w='max'
            maxH='full'
          >
            {loading && (
              <Spinner
                data-testid={`${dataTestId}-spinner`}
                size='sm'
              />
            )}
            {!loading && rightElement}
            {showClearButton && <CloseButton onClick={handleClear} />}
            {showChevron && (
              <IconButton
                aria-label='toggle menu'
                icon={
                  <ArrowDown
                    boxSize={5}
                    mx={0.5}
                    transform={isOpen ? 'rotate(180deg)' : 'rotate(0deg)'}
                    transition='all 0.2s ease-in-out'
                  />
                }
                minW='max'
                px={1}
                size='sm'
                variant='ghost'
                {...getToggleButtonProps()}
              />
            )}
          </InputRightElement>
        </InputGroup>
        <Menu
          computePositionOnMount
          isOpen={true}
          matchWidth
          onClose={onClose}
        >
          <MenuButton
            w='full'
            display={showItems ? 'block' : 'none'}
          />
          {showItems && (
            <Portal>
              <MenuList
                {...getMenuProps()}
                zIndex='modal'
                display={showItems ? 'block' : 'none'}
                border='1px'
                borderColor='gray.200'
                borderRadius='md'
                overflowY='auto'
                maxH={listMaxH}
              >
                {items.map((item, index) => (
                  <MenuItem
                    p='0'
                    key={`${item.value}${index}`}
                    {...getItemProps({ item, index })}
                  >
                    <Option
                      inputValue={inputValue}
                      isSelected={highlightedIndex === index}
                      option={item}
                    />
                  </MenuItem>
                ))}
              </MenuList>
            </Portal>
          )}
        </Menu>
      </Box>
      <FormFieldMeta
        errors={errors}
        helperText={helperText}
        helperTextStyles={helperTextStyles}
        name={name}
        {...errorContainerStyles}
      />
    </FormControl>
  );
};

export default Autocomplete;
export type { AutocompleteProps };
export type { AutocompleteOption } from './DefaultOption';
